import React, { useMemo, useState } from 'react';

import { IconButton, Select, makeStyles } from '@material-ui/core';

import { ReactComponent as QuestionMarkIcon } from 'assets/icons/general/question-mark.svg';
import FiltersFormControl from 'components/FiltersFormControl/FiltersFormControl';
import SheetsTimerHelpDialog from 'components/_dialogs/SheetsTimerHelpDialog/SheetsTimerHelpDialog';
import {
  SHEETS_TIMER_MINUTES,
  SHEETS_TIMER_NO_VALUE,
  formatSheetsTimerLabel,
  minutesToSelectValue,
  selectValueToMinutes,
} from 'constants/dictionaries/sheetsTimer';
import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import { useUserDataContext } from 'context/UserDataContext';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import { useFeatureFlag } from 'hooks/useFeatureFlags/useFeatureFlags';
import parseValuesToSelect from 'services/common/parseValuesToSelect/parseValuesToSelect';
import useSheetsTimerStore from 'storages/sheetsTimer/sheetsTimer';

import FiltersBlock from 'uniqueComponents/Exercises/ExercisesFilters/FiltersBlock/FiltersBlock';

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  selectControl: {
    flexGrow: 1,
    minWidth: 0,
  },
  helpButton: {
    flexShrink: 0,
    padding: theme.spacing(1),
  },
  helpIcon: {
    width: '1.25rem',
    height: '1.25rem',
    fill: theme.palette.primary.main,
  },
}));

function SheetsTimer() {
  const classes = useStyles();
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.SHEETS_TIMER);
  const { isMobile } = useUserDataContext();
  const { logEvent } = useUserLogContext();
  const { durationMinutes, setDurationMinutes } = useSheetsTimerStore();
  const [helpOpen, setHelpOpen] = useState(false);

  const selectValue = useMemo(() => minutesToSelectValue(durationMinutes), [durationMinutes]);

  const timerOptions = useMemo(
    () => [
      { value: SHEETS_TIMER_NO_VALUE, label: 'Bez timera' },
      ...SHEETS_TIMER_MINUTES.map(minutes => ({
        value: minutes,
        label: formatSheetsTimerLabel(minutes),
      })),
    ],
    [],
  );

  if (!isEnabled) {
    return null;
  }

  const onChange = ({ target: { value } }) => {
    const normalizedValue = value === SHEETS_TIMER_NO_VALUE ? SHEETS_TIMER_NO_VALUE : Number(value);
    const minutes = selectValueToMinutes(normalizedValue);

    logEvent('select-filter-click', {
      filter: 'Arkusze - timer',
      value: minutes == null ? 'Bez timera' : `${minutes} min`,
      feature: 'sheets',
    });
    setDurationMinutes(minutes);
  };

  const onHelpOpen = () => {
    setHelpOpen(true);
  };

  const onHelpClose = () => {
    setHelpOpen(false);
  };

  return (
    <>
      <FiltersBlock heading='Timer'>
        <div className={classes.row}>
          <FiltersFormControl className={classes.selectControl} size='small' variant='outlined'>
            <Select displayEmpty native={isMobile} onChange={onChange} value={selectValue}>
              {parseValuesToSelect({ data: timerOptions, isMobile })}
            </Select>
          </FiltersFormControl>
          <IconButton
            aria-label='Informacje o czasie trwania egzaminu maturalnego'
            className={classes.helpButton}
            onClick={onHelpOpen}
            size='small'
          >
            <QuestionMarkIcon className={classes.helpIcon} />
          </IconButton>
        </div>
      </FiltersBlock>
      <SheetsTimerHelpDialog onClose={onHelpClose} open={helpOpen} />
    </>
  );
}

export default SheetsTimer;
